import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { useAuth } from '../../contexts/AuthContext';
import {
  getValidationJobs,
  getValidationPayload,
  saveValidationRows,
  getWeeklyDates,
  confirmValidation,
  getPublishDates,
  setPublishApply,
} from '../../lib/api/admin-validation-api';
import './ValidationManagement.css';

ModuleRegistry.registerModules([AllCommunityModule]);

// ===== §6-1 규칙 계산 (그대로 이식) =====
function toNum(v) {
  if (v == null) return null;
  const s = String(v).replace(/[^\d.-]/g, '');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
function parsePtpArea(v) {
  const m = String(v || '').match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}
function applyRuleFlags(rows, thresholdWon) {
  (rows || []).forEach((r) => { r.__alert_1 = r.__alert_2 = r.__alert_3 = r.__alert_any = false; });
  // 규칙1: 전세>매매 / 규칙2: 실거래 차이 ≥ 임계
  (rows || []).forEach((r) => {
    const sale = toNum(r.article_trade_price);
    const lease = toNum(r.article_lease_price);
    if (sale !== null && lease !== null && lease > sale) r.__alert_1 = true;
    const avgSale = toNum(r.avg_trade_price);
    const avgLease = toNum(r.avg_lease_price);
    const saleGap = (sale !== null && avgSale !== null) ? Math.abs(sale - avgSale) : null;
    const leaseGap = (lease !== null && avgLease !== null) ? Math.abs(lease - avgLease) : null;
    if ((saleGap !== null && saleGap >= thresholdWon) || (leaseGap !== null && leaseGap >= thresholdWon)) r.__alert_2 = true;
  });
  // 규칙3: 같은 단지 내 작은 평수가 큰 평수보다 매매/전세가 높음
  const grouped = new Map();
  (rows || []).forEach((r) => {
    const k = String(r.complex_no || '').trim();
    if (!k) return;
    if (!grouped.has(k)) grouped.set(k, []);
    grouped.get(k).push(r);
  });
  grouped.forEach((arr) => {
    const typed = arr
      .map((r) => ({ row: r, area: parsePtpArea(r.ptp_no), sale: toNum(r.article_trade_price), lease: toNum(r.article_lease_price) }))
      .filter((x) => x.area !== null)
      .sort((a, b) => a.area - b.area);
    for (let i = 0; i < typed.length; i += 1) {
      const small = typed[i];
      for (let j = i + 1; j < typed.length; j += 1) {
        const large = typed[j];
        if (small.sale !== null && large.sale !== null && small.sale > 0 && large.sale > 0 && small.sale > large.sale) small.row.__alert_3 = true;
        if (small.lease !== null && large.lease !== null && small.lease > 0 && large.lease > 0 && small.lease > large.lease) small.row.__alert_3 = true;
      }
    }
  });
  (rows || []).forEach((r) => { r.__alert_any = !!(r.__alert_1 || r.__alert_2 || r.__alert_3); });
}

const PINNED_LEFT = ['complex_no', 'complex_name', 'ptp_no'];

const aptrankTheme = themeQuartz.withParams({
  fontFamily: 'Pretendard, Apple SD Gothic Neo, Malgun Gothic, sans-serif',
  accentColor: '#3182f6',
  headerBackgroundColor: '#f2f4f6',
  borderColor: '#e5e8eb',
});

function ValidationManagement() {
  const { token } = useAuth();
  const gridRef = useRef(null);

  const [view, setView] = useState('validate'); // 'validate' | 'history'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 작업 목록/선택
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState(() => new Set());
  const [jobModalOpen, setJobModalOpen] = useState(false); // 스크래핑 작업 선택 모달
  const [topCollapsed, setTopCollapsed] = useState(false);  // 그리드 스크롤 시 상단(탭/선택표) 숨김

  // 그리드 데이터
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  // 규칙 임계값(억) / 필터
  const [thresholdEok, setThresholdEok] = useState(1);
  const [vf, setVf] = useState({ leaseOverSale: false, gapOverReal: false, areaReverse: false });
  const thresholdWon = Math.max(0, Number(thresholdEok) || 0) * 100000000;

  // 웹사이트 표기 모달
  const [pubOpen, setPubOpen] = useState(false);
  const [weeklyDates, setWeeklyDates] = useState([]);
  const [pubSelected, setPubSelected] = useState(() => new Set());
  const [pubConfirmDate, setPubConfirmDate] = useState('');
  const [pubApplyNow, setPubApplyNow] = useState(true);

  // 확정 이력
  const [publishDates, setPublishDates] = useState([]);

  const thresholdWonRef = useRef(thresholdWon);
  thresholdWonRef.current = thresholdWon;

  // 작업 목록 로드
  const loadJobs = useCallback(async () => {
    if (!token) return;
    try { setJobs(await getValidationJobs(token, 100)); }
    catch (e) { setError(e?.message || '작업 목록 조회 실패'); }
  }, [token]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const toggleJob = (id) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // 선택된 작업(표 표기용)
  const selectedJobs = useMemo(() => (jobs || []).filter((j) => selectedJobIds.has(j.id)), [jobs, selectedJobIds]);

  // 스크래핑 작업 선택은 단건만 가능 (라디오) — 같은 항목 재클릭 시 해제
  const selectSingleJob = (id) => setSelectedJobIds((prev) => (prev.has(id) && prev.size === 1 ? new Set() : new Set([id])));

  // 작업 선택 모달 열기 (최신 목록 재조회 후)
  const openJobModal = useCallback(() => { loadJobs(); setJobModalOpen(true); }, [loadJobs]);

  // 그리드를 아래로 스크롤하면 상단(관리자 탭/검증 서브탭/선택 표)을 접어 데이터 영역을 넓힘
  const onGridBodyScroll = useCallback((e) => {
    const t = Number(e?.top) || 0;
    setTopCollapsed((prev) => {
      if (!prev && t > 48) return true;
      if (prev && t < 12) return false;
      return prev;
    });
  }, []);
  useEffect(() => {
    if (topCollapsed) document.body.classList.add('admin-scroll-collapse');
    else document.body.classList.remove('admin-scroll-collapse');
    return () => document.body.classList.remove('admin-scroll-collapse');
  }, [topCollapsed]);

  // 검증 데이터 로드
  const handleLoad = useCallback(async () => {
    const ids = [...selectedJobIds];
    if (ids.length === 0) { setError('스크래핑 작업을 1개 이상 선택하세요.'); return; }
    setError('');
    setLoading(true);
    try {
      const { rows: r, columns: c } = await getValidationPayload(token, ids);
      applyRuleFlags(r, thresholdWonRef.current);
      setColumns(c);
      setRows(r);
    } catch (e) {
      setError(e?.message || '검증 데이터 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [selectedJobIds, token]);

  const getGridRows = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return rows;
    const out = [];
    api.forEachNode((n) => out.push(n.data));
    return out;
  }, [rows]);

  const recomputeAlerts = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const all = [];
    api.forEachNode((n) => all.push(n.data));
    applyRuleFlags(all, thresholdWonRef.current);
    api.onFilterChanged();
    api.redrawRows();
  }, []);

  // 임계값 변경 시 재계산
  useEffect(() => { recomputeAlerts(); }, [thresholdWon, recomputeAlerts]);

  const columnDefs = useMemo(() => (columns || []).map((c) => ({
    field: c.field,
    headerName: c.headerName || c.field,
    hide: !c.defaultVisible,
    pinned: PINNED_LEFT.includes(c.field) ? 'left' : null,
    editable: !!c.editable,
    minWidth: c.field === 'complex_no' ? 94 : (c.field === 'complex_name' ? 190 : 130),
  })), [columns]);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  const isExternalFilterPresent = useCallback(() => (vf.leaseOverSale || vf.gapOverReal || vf.areaReverse), [vf]);
  const doesExternalFilterPass = useCallback((node) => {
    const d = node?.data || {};
    if (!(vf.leaseOverSale || vf.gapOverReal || vf.areaReverse)) return true;
    return (vf.leaseOverSale && d.__alert_1) || (vf.gapOverReal && d.__alert_2) || (vf.areaReverse && d.__alert_3);
  }, [vf]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) api.onFilterChanged();
  }, [vf]);

  const getRowStyle = useCallback((p) => {
    const d = p?.data || {};
    const c = [d.__alert_1, d.__alert_2, d.__alert_3].filter(Boolean).length;
    if (c >= 2) return { backgroundColor: '#f3e8ff' };
    if (d.__alert_1) return { backgroundColor: '#fee2e2' };
    if (d.__alert_2) return { backgroundColor: '#ffedd5' };
    if (d.__alert_3) return { backgroundColor: '#e0f2fe' };
    return null;
  }, []);

  // 검증 저장 → weekly
  const handleSave = useCallback(async (overwrite = false, baseDateArg = null) => {
    const baseDate = baseDateArg ?? (typeof window !== 'undefined' ? window.prompt('기준일 (YYYY-MM-DD)', new Date().toISOString().slice(0, 10)) : '');
    if (!baseDate) return;
    setError('');
    try {
      const res = await saveValidationRows(token, { baseDate, rows: getGridRows(), overwrite });
      if (res.needConfirm) {
        if (window.confirm(res.message || '해당 기준일에 데이터가 있습니다. 삭제 후 진행하시겠습니까?')) {
          return handleSave(true, baseDate);
        }
        return;
      }
      if (res.ok) {
        window.alert(`저장완료 ${res.count}건 (기준일 ${baseDate})`);
        loadJobs(); // 검증일 갱신 반영
      } else window.alert(res.message || '저장 실패');
    } catch (e) {
      window.alert(e?.message || '저장 실패');
    }
  }, [token, getGridRows, loadJobs]);

  // 웹사이트 표기 모달 열기
  const openPublishModal = useCallback(async () => {
    setError('');
    try {
      const dates = await getWeeklyDates(token);
      setWeeklyDates(dates);
      setPubSelected(new Set());
      setPubConfirmDate('');
      setPubApplyNow(true);
      setPubOpen(true);
    } catch (e) {
      setError(e?.message || '검증일자 조회 실패');
    }
  }, [token]);

  const togglePubDate = (baseDate) => {
    setPubSelected((prev) => {
      const next = new Set(prev);
      if (next.has(baseDate)) next.delete(baseDate); else next.add(baseDate);
      return next;
    });
  };

  const handleConfirmPublish = useCallback(async () => {
    const dates = [...pubSelected];
    if (dates.length === 0) { window.alert('검증일자를 1개 이상 선택하세요.'); return; }
    const cdate = pubConfirmDate || [...dates].sort().reverse()[0];
    if (!window.confirm(`${dates.length}개 검증일자로 확정하시겠습니까?\n동일 확정일자(${cdate}) 데이터는 삭제 후 재작성됩니다.`)) return;
    try {
      const res = await confirmValidation(token, { baseDates: dates, masterIds: [], applyNow: pubApplyNow, confirmDate: cdate });
      if (res.ok) {
        window.alert(`확정 완료: ${res.inserted}건 (기준일 ${res.validationDate}, 적용 ${res.applyNow ? 'ON' : 'OFF'})`);
        setPubOpen(false);
      } else {
        window.alert(res.message || '확정 실패');
      }
    } catch (e) {
      window.alert(e?.message || '확정 실패');
    }
  }, [pubSelected, pubConfirmDate, pubApplyNow, token]);

  // 확정 이력 로드
  const loadPublishDates = useCallback(async () => {
    setError('');
    try { setPublishDates(await getPublishDates(token)); }
    catch (e) { setError(e?.message || '확정 목록 조회 실패'); }
  }, [token]);

  useEffect(() => { if (view === 'history') loadPublishDates(); }, [view, loadPublishDates]);

  const handleToggleApply = useCallback(async (row) => {
    const next = !row.isApplied;
    if (next && !window.confirm(`${row.validationDate} 를 적용(ON) 하시겠습니까?\n기존 적용중인 기준일은 자동으로 해제(OFF)됩니다.`)) return;
    try {
      const res = await setPublishApply(token, { validationDate: row.validationDate, isApplied: next });
      if (res.ok) await loadPublishDates();
      else window.alert(res.message || '적용 변경 실패');
    } catch (e) {
      window.alert(e?.message || '적용 변경 실패');
    }
  }, [token, loadPublishDates]);

  return (
    <div className={`validation-management ${topCollapsed ? 'collapsed' : ''}`.trim()}>
      <div className="validation-subtabs">
        <button className={`validation-subtab ${view === 'validate' ? 'active' : ''}`} onClick={() => setView('validate')}>검증</button>
        <button className={`validation-subtab ${view === 'history' ? 'active' : ''}`} onClick={() => setView('history')}>웹사이트표기 조회</button>
      </div>

      {error && <div className="validation-error">{error}</div>}

      {view === 'validate' && (
        <div className="validation-body">
          {selectedJobIds.size === 0 && rows.length === 0 ? (
            <div className="validation-hero">
              <div className="validation-hero-title">검증할 데이터를 선택해주세요</div>
              <button className="validation-btn primary lg" onClick={openJobModal}>선택하기</button>
            </div>
          ) : (
          <>
          {/* 선택한 스크래핑 작업 표 + 액션 */}
          <div className="validation-selected">
            <div className="validation-selected-head">
              <span>선택한 스크래핑 작업 ({selectedJobs.length})</span>
              <div className="validation-selected-actions">
                <button className="validation-btn" onClick={openJobModal}>작업 선택/변경</button>
                <button className="validation-btn primary" onClick={handleLoad} disabled={loading || selectedJobs.length === 0}>
                  {loading ? '불러오는 중...' : '검증데이터 불러오기'}
                </button>
              </div>
            </div>
            <table className="validation-table validation-selected-table">
              <thead><tr><th>작업명</th><th>검증일</th><th>상태</th><th>성공/전체</th><th>생성일</th><th aria-label="제거" /></tr></thead>
              <tbody>
                {selectedJobs.length === 0 && <tr><td colSpan={6} className="validation-empty">선택된 작업이 없습니다. [작업 선택/변경]으로 추가하세요.</td></tr>}
                {selectedJobs.map((j) => (
                  <tr key={j.id}>
                    <td>{j.jobName || `작업 #${j.id}`}</td>
                    <td>{j.verifiedAt ? j.verifiedAt.slice(0, 16) : '-'}</td>
                    <td>{j.status || '-'}</td>
                    <td className="num">{(j.successCount ?? 0)}/{(j.totalCount ?? 0)}</td>
                    <td>{j.createdAt || ''}</td>
                    <td><button className="validation-row-x" onClick={() => toggleJob(j.id)} title="선택 해제">×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 툴바 */}
          <div className="validation-toolbar">
            <label className="tb-item">기준금액(억)
              <input type="number" min="0" step="0.1" value={thresholdEok}
                onChange={(e) => setThresholdEok(e.target.value)} style={{ width: 70, marginLeft: 6 }} />
            </label>
            <label className="tb-item"><input type="checkbox" checked={vf.leaseOverSale}
              onChange={(e) => setVf((p) => ({ ...p, leaseOverSale: e.target.checked }))} /> 전세&gt;매매</label>
            <label className="tb-item"><input type="checkbox" checked={vf.gapOverReal}
              onChange={(e) => setVf((p) => ({ ...p, gapOverReal: e.target.checked }))} /> 실거래 차이</label>
            <label className="tb-item"><input type="checkbox" checked={vf.areaReverse}
              onChange={(e) => setVf((p) => ({ ...p, areaReverse: e.target.checked }))} /> 평수 역전</label>
            <div className="tb-spacer" />
            <button className="validation-btn" onClick={() => handleSave(false)} disabled={rows.length === 0}>검증저장</button>
            <button className="validation-btn primary" onClick={openPublishModal}>웹사이트 표기</button>
          </div>

          {/* 그리드 */}
          <div className="validation-grid ag-theme-validation">
            <AgGridReact
              ref={gridRef}
              theme={aptrankTheme}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowStyle={getRowStyle}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesExternalFilterPass}
              onCellValueChanged={recomputeAlerts}
              onBodyScroll={onGridBodyScroll}
              stopEditingWhenCellsLoseFocus
              suppressColumnVirtualisation
            />
          </div>
          </>
          )}
        </div>
      )}

      {view === 'history' && (
        <div className="validation-history">
          <div className="validation-history-head">
            <span>확정 기준일 목록 (웹 노출은 적용 ON 1개만)</span>
            <button className="validation-btn" onClick={loadPublishDates}>새로고침</button>
          </div>
          <table className="validation-table">
            <thead>
              <tr><th>검증일자</th><th>행수</th><th>단지수</th><th>확정시각</th><th>적용</th></tr>
            </thead>
            <tbody>
              {publishDates.length === 0 && <tr><td colSpan={5} className="validation-empty">확정된 데이터가 없습니다.</td></tr>}
              {publishDates.map((row) => (
                <tr key={row.validationDate} className={row.isApplied ? 'applied' : ''}>
                  <td>{row.validationDate}</td>
                  <td className="num">{(row.rowCount || 0).toLocaleString()}</td>
                  <td className="num">{(row.complexCount || 0).toLocaleString()}</td>
                  <td>{row.confirmedAt || '-'}</td>
                  <td>
                    <button className={`validation-toggle ${row.isApplied ? 'on' : 'off'}`} onClick={() => handleToggleApply(row)}>
                      {row.isApplied ? 'ON' : 'OFF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 스크래핑 작업 선택 모달 */}
      {jobModalOpen && (
        <div className="validation-modal-bg" onClick={() => setJobModalOpen(false)}>
          <div className="validation-modal xwide" onClick={(e) => e.stopPropagation()}>
            <div className="validation-modal-h">스크래핑 작업 선택</div>
            <div className="validation-modal-b">
              <table className="validation-table validation-job-table">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>선택</th>
                    <th>작업명</th>
                    <th>검증일</th>
                    <th>상태</th>
                    <th>성공/전체</th>
                    <th>생성일</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length === 0 && <tr><td colSpan={6} className="validation-empty">작업이 없습니다.</td></tr>}
                  {jobs.map((j) => {
                    const checked = selectedJobIds.has(j.id);
                    return (
                      <tr key={j.id} className={checked ? 'selected' : ''} onClick={() => selectSingleJob(j.id)}>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input type="radio" name="validation-job-pick" checked={checked} onChange={() => selectSingleJob(j.id)} />
                        </td>
                        <td className="job-name-cell">{j.jobName || `작업 #${j.id}`}</td>
                        <td>{j.verifiedAt ? j.verifiedAt.slice(0, 16) : '-'}</td>
                        <td>{j.status || '-'}</td>
                        <td className="num">{(j.successCount ?? 0)}/{(j.totalCount ?? 0)}</td>
                        <td>{j.createdAt || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="validation-modal-f">
              <span className="validation-modal-count">선택 {selectedJobIds.size}개</span>
              <button className="validation-btn" onClick={() => setJobModalOpen(false)}>닫기</button>
              <button className="validation-btn primary" onClick={() => setJobModalOpen(false)}>선택완료</button>
            </div>
          </div>
        </div>
      )}

      {/* 웹사이트 표기 모달 */}
      {pubOpen && (
        <div className="validation-modal-bg" onClick={() => setPubOpen(false)}>
          <div className="validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="validation-modal-h">웹사이트 표기 — 검증일자 선택</div>
            <div className="validation-modal-b">
              <table className="validation-table">
                <thead><tr><th>선택</th><th>검증일자</th><th>건수</th></tr></thead>
                <tbody>
                  {weeklyDates.length === 0 && <tr><td colSpan={3} className="validation-empty">저장된 검증일자가 없습니다.</td></tr>}
                  {weeklyDates.map((d) => (
                    <tr key={d.baseDate}>
                      <td><input type="checkbox" checked={pubSelected.has(d.baseDate)} onChange={() => togglePubDate(d.baseDate)} /></td>
                      <td>{d.baseDate}</td>
                      <td className="num">{(d.rowCount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="validation-modal-opts">
                <label>확정일자 <input type="date" value={pubConfirmDate} onChange={(e) => setPubConfirmDate(e.target.value)} /></label>
                <label><input type="checkbox" checked={pubApplyNow} onChange={(e) => setPubApplyNow(e.target.checked)} /> 확정 후 즉시 적용(ON)</label>
              </div>
            </div>
            <div className="validation-modal-f">
              <button className="validation-btn" onClick={() => setPubOpen(false)}>닫기</button>
              <button className="validation-btn primary" onClick={handleConfirmPublish}>확정</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ValidationManagement;
